/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useState } from "react"
import { useEnsureGlobalState } from "./GlobalStateProvider"
import { SageLogo } from "../stories/quoteOrders/SageLogo"
import { BaseButton, Icon, IconButton, List } from "@cd2/components/build/es6"
import { colors } from "./EOSThemeProvider"
import { icons } from "../constants/icons"
import { pollingIntervals } from "../constants/pollingIntervals"
import { DropdownMenu } from "./DropdownMenu"
import { ISageImportOptMap, sageImportOpts } from "../constants/sageImportOpts"
import { getWarehouseSageIds } from "../Pages/SageSyncPage"
import { set } from "date-fns"

type ISageStatus =
  | "pending"
  | "in_progress"
  | "saving"
  | "saving_partial"
  | "processing"
  | "processing_now"
  | "completed"
  | "errored"

export function SageImportButton({ compact }: { compact: boolean }) {
  const [interestedInSageId, setInterestedInSageId] = useState<string | null>(null)
  const { currentUser } = useEnsureGlobalState()
  const skeem = window.skeem

  const [sageStatus, setSageStatus] = React.useState<ISageStatus>("completed")

  useEffect(() => {
    if (!interestedInSageId) {
      return
    }

    let isActive = true

    const tick = async () => {
      const importData = await getSageStatus(interestedInSageId)

      if (!isActive) {
        return
      }

      setSageStatus(importData.status)

      // if we have finished or there is no import then we are no longer
      // interested in status changes
      if (importData.status === "completed" || importData.status === "errored") {
        setInterestedInSageId(null)
      }
    }

    tick()
    const intervalId = setInterval(tick, pollingIntervals.sageImportStatus)

    return () => {
      isActive = false
      clearInterval(intervalId)
    }
  }, [interestedInSageId])

  async function getSageStatus(id: string) {
    const data = await skeem.fetch2<{ status: ISageStatus }>({
      first: {
        name: "sage_imports",
        attributes: ["status"],
        filter: {
          matches: { branch: currentUser.branch.id, id },
        },
      },
    })

    return data ? data : { status: "completed" as ISageStatus }
  }

  async function initiateConnection(sageImportObject?: ISageImportOptMap) {
    if (currentUser.canEditGlobalUserPermissions) {
      const runningSageImports = await skeem.fetch2<{ id: string; status: ISageStatus }>({
        first: {
          name: "sage_imports",
          filter: {
            and: [{ matches: { branch: currentUser.branch.id } }, { matches: { status: "pending" } }],
          },
        },
      })
      if (runningSageImports) {
        setSageStatus(runningSageImports.status)
        setInterestedInSageId(runningSageImports.id)
      } else {
        const sageWarehouseIds = await getWarehouseSageIds(skeem, currentUser.branch.id)
        const newImport = await skeem.mutate({
          sage_imports: {
            create: {
              status: "pending",
              import_object: {
                ...sageImportObject,
                productLocationWarehouseSageIds: sageWarehouseIds,
              },
              branch: { set: currentUser.branch.id },
              user: { set: skeem.currentSession.id },
            },
          },
        })
        setSageStatus("pending")
        setInterestedInSageId(newImport.id)
      }
    } else {
      setSageStatus("pending")
      setTimeout(() => {
        setSageStatus("completed")
      }, 60000)
    }
  }

  const isRunningImport = [
    "pending",
    "in_progress",
    "saving",
    "saving_partial",
    "processing",
    "processing_now",
  ].includes(sageStatus)

  return (
    <BaseButton
      onClick={() => initiateConnection(sageImportOpts.lite)}
      renderLeft={() => (
        <List
          css={css`
            padding: 0 6px;
          `}
          horizontal
        >
          <Icon icon={icons.refresh} spin={isRunningImport} />
          <div style={{ width: 40, position: "relative", height: 10 }}>
            <SageLogo color="#FFF" />
          </div>
        </List>
      )}
      renderRight={() => (
        <div onClick={e => e.stopPropagation()}>
          <DropdownMenu
            items={[
              currentUser.isSuperAdmin && {
                children: "Fuller sync",
                onClick: () => initiateConnection(sageImportOpts.fuller),
              },
              { children: "Full sync", onClick: () => initiateConnection(sageImportOpts.lite) },
              {
                children: "Quick sync",
                onClick: () => initiateConnection(sageImportOpts.lite),
              },
              {
                children: "Stock sync",
                onClick: () => initiateConnection(sageImportOpts.stockOnly),
              },
            ].filter(Boolean)}
          >
            <IconButton icon={icons.chevronDown} buttonStyle="text" color="white" />
          </DropdownMenu>
        </div>
      )}
      theme={{
        buttonColor: colors.sage,
        padding: { topAndBottom: 12, sides: 2 },
      }}
    >
      {!compact && (
        <React.Fragment>
          {["pending", "in_progress"].includes(sageStatus) && "Reading Data"}
          {["saving", "saving_partial", "processing", "processing_now"].includes(sageStatus) &&
            "Saving Data"}
          {["errored", "completed"].includes(sageStatus) && "Sync"}
        </React.Fragment>
      )}
    </BaseButton>
  )
}
